import {
  SILENCE_VOLUME_THRESHOLD,
  SILENCE_PERIOD_THRESHOLD,
} from './ContactListeningConstants';

export const CANVAS_BAR_WIDTH = 5;

export enum CanvasState {
  NO_DATA,
  PREPARING,
  READY,
  ERROR,
}

export enum ContactListeningTab {
  CONTACT = 'contact',
  ESCALATION = 'escalation',
}

export enum ContactListeningSubTab {
  PHONE_RECORDING = 'phone-recording',
  CHAT_HISTORY = 'chat-history',
  EMAIL_TRANSCRIPT = 'email-transcript',
}

export interface SampledAudio {
  duration: number;
  gaps: Gap[];
  leftSamples: number[];
  leftMax: number;
  rightSamples: number[];
  rightMax: number;
  // -1 if not in gap
  // otherwise index where the gap ends.
  gapsArray: number[];
}

interface Gap {
  start: number;
  endx: number;
}

export interface CLClickstream {
  tag1?: string;
  tag2?: string;
  tag3?: string;
  tag4?: string;
  tag5?: string;
  slotID: string;
  contactID?: string;
}

export interface CLClickstreamWithMediaLegInfo extends CLClickstream {
  // Media legs
  // Ref: https://quip-amazon.com/Jnz2AF2IH89R/SPIKE-Inspire-Call-Recording-Compliance-for-Italy

  connectContactId?: string; // Media leg for Connect contacts
}

export const defaultCLClickstream: CLClickstream = {
  slotID: 'open-contact-listening-modal',
  tag1: 'Team',
  tag2: 'Coaching Table',
  tag3: 'Contact Review',
};

export const processBuffer = (audioBuffer: AudioBuffer): SampledAudio => {
  const leftChannel = audioBuffer.getChannelData(0);
  const rightChannel = audioBuffer.getChannelData(1);
  const durationInSec = audioBuffer.duration;
  const sampleRate = audioBuffer.sampleRate;

  const leftSamples: number[] = [];
  const rightSamples: number[] = [];
  const gaps: Gap[] = [];

  // 1 samples per second
  const samples = Math.ceil(durationInSec);

  let inSilence = false;
  let silenceStart = 0;

  let leftMax = 0;
  let rightMax = 0;
  for (let i = 0; i < samples; ++i) {
    const start = sampleRate * i;
    let sumLeft = 0;
    let sumRight = 0;
    for (let j = 0; j < sampleRate; ++j) {
      const index = start + j;
      sumLeft += Math.abs(leftChannel[index]);
      sumRight += Math.abs(rightChannel[index]);
    }
    if (sumLeft > leftMax) {
      leftMax = sumLeft;
    }
    if (sumRight > rightMax) {
      rightMax = sumRight;
    }
    leftSamples.push(sumLeft);
    rightSamples.push(sumRight);
    if (sumLeft + sumRight < SILENCE_VOLUME_THRESHOLD) {
      if (!inSilence) {
        inSilence = true;
        silenceStart = i;
      }
    } else {
      if (inSilence) {
        inSilence = false;
        const silenceDuration = i - silenceStart;
        if (silenceDuration > SILENCE_PERIOD_THRESHOLD) {
          gaps.push({ start: silenceStart, endx: i });
        }
      }
    }
  }
  const gapsArray: number[] = [];
  let gapNr = 0;
  if (gaps.length > 0) {
    let gap = gaps[0];
    for (let i = 0; i < leftSamples.length; ++i) {
      if (i < gap.start) {
        // before the next gap
        gapsArray.push(-1);
      } else {
        if (i < gap.endx) {
          // in the gap
          gapsArray.push(gap.endx);
        } else {
          // just at finished a gap or beyond all the gaps
          gapsArray.push(-1);
          if (i === gap.endx) {
            gapNr += 1;
            if (gapNr < gaps.length) {
              gap = gaps[gapNr];
            }
          }
        }
      }
    }
  }
  return {
    duration: durationInSec,
    gaps: gaps,
    leftSamples: leftSamples,
    leftMax: leftMax,
    rightSamples: rightSamples,
    rightMax: rightMax,
    gapsArray: gapsArray,
  };
};
